import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { AdCard, type AdCardData } from "@/components/AdCard";
import { listAds } from "@/lib/ads.functions";
import { useLocale } from "@/lib/i18n/locale-context";
import { CATEGORIES, categoryLabel } from "@/lib/categories";

const BASE = "https://sellavo.com";

/** Kort säljtext per kategori – används i intro och metabeskrivning. */
const BLURBS: Record<string, string> = {
  fordon: "bilar, mopeder, cyklar och tillbehör",
  bostad: "bostäder, tomter och fritidshus",
  "for-hemmet": "möbler, belysning, köksprylar och inredning",
  elektronik: "mobiler, datorer, tv och ljud",
  personligt: "personliga prylar, väskor och accessoarer",
  "fritid-hobby": "friluftsprylar, instrument, spel och böcker",
  affarsverksamhet: "företagsutrustning, inventarier och maskiner",
  "hobby-samlarprylar": "samlarobjekt, vintage och kuriosa",
  "verktyg-bygg": "verktyg, maskiner och byggmaterial",
  "sport-traning": "träningsutrustning, cyklar och sportprylar",
  barnartiklar: "barnvagnar, leksaker, barnkläder och babyutrustning",
  djur: "tillbehör till hund, katt, häst och smådjur",
  tradgard: "trädgårdsmöbler, redskap, växter och maskiner",
  "mode-skonhet": "kläder, skor, väskor och skönhetsprylar",
  ovrigt: "prylar som inte passar i någon annan kategori",
};

export const Route = createFileRoute("/salja/$kategori")({
  loader: async ({ params }) => {
    const category = CATEGORIES.find((c) => c.slug === params.kategori);
    if (!category) throw notFound();
    // Kategoritexter och guider ligger i egna paket – hämtas bara här.
    const [{ categorySeo }, { GUIDES }] = await Promise.all([
      import("@/lib/kategori-seo"),
      import("@/lib/guides"),
    ]);
    const related = GUIDES.filter((g) => g.category === category.slug);
    return {
      slug: category.slug,
      label: category.label,
      seo: categorySeo(category.slug) ?? null,
      guides: related.length ? related : GUIDES.slice(0, 3),
    };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Kategorin finns inte | Sellavo" }, { name: "robots", content: "noindex" }],
      };
    }
    const label = loaderData.label;
    const blurb = BLURBS[loaderData.slug] ?? "begagnade prylar";
    const title = `Sälja ${label.toLowerCase()} på nätet – gratis annons | Sellavo`;
    const description = `Sälj och köp ${blurb} begagnat. Fota prylen så skriver AI:n annonsen och föreslår pris – gratis att komma igång på Sellavo.`;
    const url = `${BASE}/salja/${params.kategori}`;
    const seo = loaderData.seo;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        ...(seo
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: seo.faq.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              },
            ]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Start", item: BASE },
              { "@type": "ListItem", position: 2, name: "Annonser", item: `${BASE}/annonser` },
              { "@type": "ListItem", position: 3, name: label, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: CategoryLanding,
  errorComponent: ({ error }) => (
    <main className="mx-auto w-full max-w-4xl px-4 py-16 sm:px-6 lg:px-8" role="alert">
      <p className="text-muted-foreground">{error.message}</p>
    </main>
  ),
  notFoundComponent: () => (
    <main className="mx-auto w-full max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
      <h1 className="font-heading text-2xl font-extrabold text-foreground">Kategorin finns inte</h1>
      <Button asChild className="mt-6">
        <Link to="/annonser">Se alla annonser</Link>
      </Button>
    </main>
  ),
});

function CategoryLanding() {
  const { slug, label, seo, guides } = Route.useLoaderData();
  const fetchAds = useServerFn(listAds);
  const { country: market } = useLocale();
  const blurb = BLURBS[slug] ?? "begagnade prylar";

  const { data: ads } = useQuery({
    queryKey: ["landing-ads", slug, market],
    queryFn: () => fetchAds({ data: { category: slug, limit: 12, country: market } }),
    staleTime: 60_000,
  });


  return (
    <main className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <nav aria-label="Brödsmulor" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Start
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/annonser" className="hover:text-foreground">
          Annonser
        </Link>
      </nav>

      <h1 className="mt-3 max-w-3xl font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl">
        Sälja {label.toLowerCase()} på nätet
      </h1>
      {seo ? (
        <div className="mt-6 max-w-2xl rounded-lg border border-primary/25 bg-primary/5 p-5">
          <p className="font-heading text-[11px] font-bold uppercase tracking-wide text-primary">
            Det korta svaret
          </p>
          <p className="mt-2 text-sm leading-relaxed text-foreground sm:text-base">{seo.summary}</p>
        </div>
      ) : null}

      {(
        seo?.intro ?? [
          `Här köper och säljer privatpersoner och företag ${blurb} begagnat. Fota prylen med mobilen – vi granskar bilden, skriver rubrik och beskrivning och föreslår ett rimligt pris.`,
        ]
      ).map((p) => (
        <p
          key={p}
          className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg"
        >
          {p}
        </p>
      ))}

      <div className="mt-6 flex flex-wrap gap-3">
        <Button asChild>
          <Link to="/skapa">Sälj i {label.toLowerCase()}</Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/annonser" search={{ kategori: slug }}>
            Se alla annonser i kategorin
          </Link>
        </Button>
      </div>

      <section className="mt-12">
        <h2 className="font-heading text-xl font-bold text-foreground">
          Senaste annonserna i {label.toLowerCase()}
        </h2>
        {ads && ads.length ? (
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(ads as AdCardData[]).map((ad, i) => (
              <AdCard key={ad.slug} ad={ad} priority={i === 0} />
            ))}
          </div>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground">
            Inga annonser i kategorin just nu – lägg upp den första.
          </p>
        )}
      </section>

      <section className="mt-14">
        <h2 className="font-heading text-xl font-bold text-foreground">
          Så säljer du {label.toLowerCase()} snabbt
        </h2>
        <ol className="mt-4 grid gap-4 sm:grid-cols-3">
          {[
            {
              t: "1. Fota eller filma",
              d: "Dagsljus, ren bakgrund och närbilder på skador. Filmar du plockar vi ut varje pryl automatiskt.",
            },
            {
              t: "2. Låt AI:n skriva",
              d: "Vi föreslår rubrik, beskrivning, kategori och pris. Du ändrar precis det du vill.",
            },
            {
              t: "3. Publicera och sälj",
              d: "Köparna hör av sig i chatten. Ni gör upp om upphämtning eller frakt med spårning.",
            },
          ].map((step) => (
            <li key={step.t} className="rounded-lg border border-border bg-card p-5">
              <p className="font-heading text-base font-semibold text-foreground">{step.t}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.d}</p>
            </li>
          ))}
        </ol>
      </section>

      {seo ? (
        <>
          {seo.sections.map((section) => (
            <section key={section.heading} className="mt-14">
              <h2 className="font-heading text-xl font-bold text-foreground">{section.heading}</h2>
              {section.body.map((p) => (
                <p
                  key={p}
                  className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base"
                >
                  {p}
                </p>
              ))}
            </section>
          ))}

          <section className="mt-14">
            <h2 className="font-heading text-xl font-bold text-foreground">
              Vad brukar {label.toLowerCase()} gå för?
            </h2>
            <div className="mt-4 max-w-2xl overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th scope="col" className="px-4 py-2.5 text-left font-heading font-semibold">
                      Pryl
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-left font-heading font-semibold">
                      Ungefärligt pris begagnat
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {seo.priceGuide.map((row) => (
                    <tr key={row.item} className="border-t border-border">
                      <td className="px-4 py-2.5 text-foreground">{row.item}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{row.span}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Ungefärliga spann. Skick, märke och efterfrågan där du bor styr mest – låt gärna
              djupvärderingen titta på just din pryl.
            </p>
          </section>

          <section className="mt-14">
            <h2 className="font-heading text-xl font-bold text-foreground">Vanliga frågor</h2>
            <dl className="mt-4 max-w-3xl space-y-4">
              {seo.faq.map((f) => (
                <div key={f.q} className="rounded-lg border border-border bg-card p-5">
                  <dt className="font-heading text-base font-semibold text-foreground">{f.q}</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</dd>
                </div>
              ))}
            </dl>
          </section>
        </>
      ) : null}

      <section className="mt-14">
        <h2 className="font-heading text-xl font-bold text-foreground">Läs mer</h2>
        <ul className="mt-3 space-y-2">
          {guides.map((g) => (
            <li key={g.slug}>
              <Link
                to="/guider/$slug"
                params={{ slug: g.slug }}
                className="text-sm text-primary hover:underline"
              >
                {g.title}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-14">
        <h2 className="font-heading text-base font-bold text-foreground">Fler kategorier</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {CATEGORIES.filter((c) => c.slug !== slug).map((c) => (
            <Link
              key={c.slug}
              to="/salja/$kategori"
              params={{ kategori: c.slug }}
              className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
            >
              {categoryLabel(c.slug)}
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
